const getDataHookAtIndex = (
  dataHook: string,
  index: number | string = 0,
): string => `${dataHook}-${index}`;

const getDataHook = (dataHook: string, index: string | number | undefined) =>
  index ? getDataHookAtIndex(dataHook, index) : dataHook;
enum TemplateItem {
  Self = 'template-item',
  Title = 'template-item-title',
  Subtitle = 'template-item-subtitle',
  FullDescription = 'template-full-desc',
  Badge = 'template-badge',
  Tag = 'tag',
  ThumbnailImage = 'template-grid-item-thumbnail-image',
  Wrapper = 'template-item-wrapper',
}
enum MainPage {
  Page = 'templates-main-page',
  HeaderTitle = 'templates-main-page-title',
  HeaderPriorTitle = 'templates-main-page-prior-title',
  HeaderDescription = 'templates-main-page-header-description',
  SideBar = 'templates-main-page-sidebar',
  SideBarTitle = 'templates-main-page-sidebar-title',
  Tab = 'templates-main-page-tab',
  GridTitle = 'templates-main-page-grid-title',
  GridSubtitle = 'templates-main-page-grid-subtitle',
  GridDocs = 'templates-main-page-grid-docs',
}
enum TemplatePage {
  Page = 'template-page',
  BackCrumb = 'back-crumb',
  TagListWrapper = 'tag-list-wrapper',
  ChevronLeftStickyHeader = 'chevron-left-sticky-header',
  MainCTAsWrapper = 'main-ctas-wrapper',
  StickyHeaderWrapper = 'sticky-header-wrapper',
  CloneTemplateButton = 'clone-template-button',
  PreviewButton = 'preview-button',
  GitHubLink = 'github-link',
  CLIModal = 'cli-modal',
  TerminalExampleTitle = 'terminal-example-title',
  TerminalExampleCopyButton = 'terminal-example-copy-button',
  TerminalExampleClonePrompt = 'terminal-example-clone-prompt',
  PackageManagerPopover = 'package-manager-popover',
  PackageManagerButton = 'package-manager-button',
  PackageManagerMenuLayout = 'package-manager-menu-layout',
  CLIModalLayout = 'cli-modal-layout',
  CLIModalPreStepsList = 'cli-modal-pre-steps-list',
  MainCardLayout = 'main-card-layout',
  DescriptionTagsTitle = 'description-tags-title',
  HowToBuild = 'how-to-build',
  InstructionsWrapper = 'instructions-wrapper',
  InstructionsTitle = 'instructions-title',
  InstructionsText = 'instructions-text',
  ImagesCarousel = 'images-carousel',
  SelfHostedModal = 'self-hosted-modal',
  SelfHostedModalLayout = 'self-hosted-modal-layout',
  DeploymentProcessTitle = 'deployment-process-title',
  SelfHostModalAppNameInput = 'self-host-modal-app-name-input',
  SelfHostModalMoreInfoDoc = 'self-host-modal-more-info-doc',
  SelfHostModalBaseUrlInput = 'self-host-modal-base-url-input',
  SelfHostModalTestYourAppTitle = 'self-host-modal-test-your-app-title',
  SelfHostModalTestYourAppEnvVarText = 'self-host-modal-test-your-app-env-var-text',
  SelfHostedModalStepper = 'self-host-modal-stepper',
  SelfHostedModalSnippet = 'self-hosted-modal-snippet',
  EnvVariablesLink = 'env-variables-link',
}

enum CollapsibleTextBox {
  ReadMore = 'read-more-text-button',
  TextContainer = 'collapsible-text-container',
}

enum MarkDowns {
  CodeText = 'code-text',
  CodeBox = 'code-box',
  CodeBlockCopyToClipboard = 'code-block-copy-to-clipboard',
  CodeBlockCopyToClipboardTextButton = 'code-block-copy-to-clipboard-text-button',
  BlockquoteTitle = 'blockquote-title',
  BlockquoteSectionHelper = 'blockquote-section-helper',
  BlockquoteTextContent = 'blockquote-text-content',
  ImageMarkDown = 'image-markdown',
}

export const DataHooks = {
  Loader: 'pre-loader',
  MainPage: MainPage.Page,
  MainPageTitle: MainPage.HeaderTitle,
  MainPagePriorTitle: MainPage.HeaderPriorTitle,
  MainPageHeaderDescription: MainPage.HeaderDescription,
  MainPageSideBar: MainPage.SideBar,
  MainPageSideBarTitle: MainPage.SideBarTitle,
  TabItem: (item: string) => `${MainPage.Tab}-${item}`,
  TemplateTitle: (index?: number) => getDataHook(TemplateItem.Title, index),
  TemplateSubtitle: (index: number) =>
    getDataHookAtIndex(TemplateItem.Subtitle, index),
  TemplateFullDescription: TemplateItem.FullDescription,
  TemplateBadge: (index?: number) => getDataHook(TemplateItem.Badge, index),
  DescriptionTagsTitle: TemplatePage.DescriptionTagsTitle,
  HowToBuildWrapper: `${TemplatePage.HowToBuild}-wrapper`,
  ImagesCarousel: TemplatePage.ImagesCarousel,
  InstructionsWrapper: TemplatePage.InstructionsWrapper,
  InstructionsTitle: TemplatePage.InstructionsTitle,
  InstructionsText: TemplatePage.InstructionsText,
  HowToBuildTitle: `${TemplatePage.HowToBuild}-title`,
  HowToBuildDocsButton: `${TemplatePage.HowToBuild}-docs-button`,
  CollapsibleTextBoxReadMore: CollapsibleTextBox.ReadMore,
  CollapsibleTextContainer: CollapsibleTextBox.TextContainer,
  StickyHeaderWrapper: TemplatePage.StickyHeaderWrapper,
  TagListWrapper: TemplatePage.TagListWrapper,
  TemplateTag: (tagIndex: number, index?: number) =>
    getDataHookAtIndex(
      `${index && getDataHookAtIndex(TemplateItem.Self, index).concat('-')}${
        TemplateItem.Tag
      }`,
      tagIndex,
    ),
  TemplateGridItemThumbnailImage: (index: number) =>
    getDataHook(TemplateItem.ThumbnailImage, index),
  TemplateWrapper: (id: string) => getDataHookAtIndex(TemplateItem.Wrapper, id),
  GridTitle: MainPage.GridTitle,
  GridSubtitle: MainPage.GridSubtitle,
  GridDocs: MainPage.GridDocs,
  TemplatesGridWrapper: 'templates-grid-wrapper',
  BackCrumb: TemplatePage.BackCrumb,
  ChevronLeftStickyHeader: TemplatePage.ChevronLeftStickyHeader,
  BackCrumbText: `${TemplatePage.BackCrumb}-text`,
  TemplatePage: TemplatePage.Page,
  MainCTAsWrapper: TemplatePage.MainCTAsWrapper,
  CLIModal: TemplatePage.CLIModal,
  CLIModalLayout: TemplatePage.CLIModalLayout,
  CloneTemplateButton: (isStickyHeader: boolean) =>
    `${TemplatePage.CloneTemplateButton}${isStickyHeader && '-sticky'}`,
  PreviewButton: (isStickyHeader: boolean) =>
    `${TemplatePage.PreviewButton}${isStickyHeader && '-sticky'}`,
  GitHubLink: (isStickyHeader: boolean) =>
    `${TemplatePage.GitHubLink}${isStickyHeader && '-sticky'}`,
  MainCardLayout: TemplatePage.MainCardLayout,
  CLIModalPreStepsList: TemplatePage.CLIModalPreStepsList,
  TerminalExampleTitle: TemplatePage.TerminalExampleTitle,
  TerminalExampleCopyButton: TemplatePage.TerminalExampleCopyButton,
  TerminalExampleClonePrompt: TemplatePage.TerminalExampleClonePrompt,
  PackageManagerPopover: TemplatePage.PackageManagerPopover,
  PackageManagerMenuLayout: TemplatePage.PackageManagerMenuLayout,
  PackageManagerButton: TemplatePage.PackageManagerButton,
  BlockquoteTitle: MarkDowns.BlockquoteTitle,
  BlockquoteSectionHelper: MarkDowns.BlockquoteSectionHelper,
  BlockquoteTextContent: MarkDowns.BlockquoteTextContent,
  CodeText: MarkDowns.CodeText,
  CodeBox: MarkDowns.CodeBox,
  CodeBlockCopyToClipboard: MarkDowns.CodeBlockCopyToClipboard,
  CodeBlockCopyToClipboardTextButton:
    MarkDowns.CodeBlockCopyToClipboardTextButton,
  ImageMarkDown: MarkDowns.ImageMarkDown,
  SelfHostedModal: TemplatePage.SelfHostedModal,
  SelfHostedModalLayout: TemplatePage.SelfHostedModalLayout,
  DeploymentProcessTitle: TemplatePage.DeploymentProcessTitle,
  SelfHostModalAppNameInput: TemplatePage.SelfHostModalAppNameInput,
  SelfHostModalMoreInfoDoc: TemplatePage.SelfHostModalMoreInfoDoc,
  SelfHostModalBaseUrlInput: TemplatePage.SelfHostModalBaseUrlInput,
  SelfHostModalTestYourAppTitle: TemplatePage.SelfHostModalTestYourAppTitle,
  SelfHostModalTestYourAppEnvVarText:
    TemplatePage.SelfHostModalTestYourAppEnvVarText,
  SelfHostedModalStepper: TemplatePage.SelfHostedModalStepper,
  SelfHostedModalSnippet: TemplatePage.SelfHostedModalSnippet,
  EnvVariablesLink: TemplatePage.EnvVariablesLink,
  OpenBlocksFlowError: 'open-blocks-flow-error',
};
